import { useEffect, useRef } from 'react';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import useEngagementPanelMessages from '@components/engagement/useEngagementMessages';
import {
  ENGAGEMENT_PANEL_IDS,
  ENGAGEMENT_MESSAGE_TYPE
} from '@components/engagement/constants';

const LOG = LoggerFactory.create('EngagementMessagesSimulator');

/**
 * Handler function to parse the upcoming message and determine if we need to
 * show a toast, badge or both based on the message type.
 *
 * Real time updates breakdown per feature:
 *
 * `LiveQA Engagement Messages`
 * Attendee
 * ----------------------------------------------------------------
 * 1.- Toasts shows when the attendee's own question is answered
 * 2.- Toast shows when Q&A is open and it is set as custom time
 *
 * Moderator
 * ----------------------------------------------------------------
 * 1.- Badge shows when new pending questions are available
 *
 * `LivePolling Engagement Messages`
 * Attendee
 * ----------------------------------------------------------------
 * 1.- Poll question is opened
 */
const handleSessionDetailsEngagementMessages = ({
  type: msgType,
  data,
  notifyMessage,
  setActivePanel
}) => {
  switch (msgType) {
    case 'question_answered':
      // Show toast
      notifyMessage(ENGAGEMENT_PANEL_IDS.LIVE_QA, {
        msgType: ENGAGEMENT_MESSAGE_TYPE.TOAST,
        message: {
          body: '[Simulation] Your question has been answered',
          contentType: 'LinkedCallback',
          linkedContent: {
            name: 'Open Q&A',
            onDismiss: () => {
              // Show badge
              notifyMessage(ENGAGEMENT_PANEL_IDS.LIVE_QA, {
                msgType: ENGAGEMENT_MESSAGE_TYPE.BADGE
              });
            },
            onClick: () => {
              // Open Q&A panel
              setActivePanel(ENGAGEMENT_PANEL_IDS.LIVE_QA);
            }
          }
        }
      });
      break;
    case 'poll_question_opened':
      // Show toast
      notifyMessage(ENGAGEMENT_PANEL_IDS.LIVE_POLLING, {
        msgType: ENGAGEMENT_MESSAGE_TYPE.TOAST,
        message: {
          body: `[Simulation] A new poll question is available: ${data.text}`,
          contentType: 'LinkedCallback',
          linkedContent: {
            name: 'Open Polls',
            onDismiss: () => {
              // Show badge
              notifyMessage(ENGAGEMENT_PANEL_IDS.LIVE_POLLING, {
                msgType: ENGAGEMENT_MESSAGE_TYPE.BADGE
              });
            },
            onClick: () => {
              // Open polls panel
              setActivePanel(ENGAGEMENT_PANEL_IDS.LIVE_POLLING);
            }
          }
        }
      });
      break;
    case 'survey_opened':
      // Show toast
      notifyMessage(ENGAGEMENT_PANEL_IDS.SURVEY, {
        msgType: ENGAGEMENT_MESSAGE_TYPE.TOAST,
        message: {
          body: '[Simulation] Please provide your feedback for this session!',
          contentType: 'LinkedCallback',
          linkedContent: {
            name: 'Open Survey',
            onDismiss: () => {
              // Show badge
              notifyMessage(ENGAGEMENT_PANEL_IDS.SURVEY, {
                msgType: ENGAGEMENT_MESSAGE_TYPE.BADGE
              });
            },
            onClick: () => {
              setActivePanel(ENGAGEMENT_PANEL_IDS.SURVEY);
            }
          }
        }
      });
      break;
    case 'chat_started':
      // Show toast
      notifyMessage(ENGAGEMENT_PANEL_IDS.CHAT, {
        msgType: ENGAGEMENT_MESSAGE_TYPE.TOAST,
        message: {
          body: '[Simulation] Session chat is now opened!',
          contentType: 'LinkedCallback',
          linkedContent: {
            name: 'Open Chat',
            onDismiss: () => {
              // Show badge
              notifyMessage(ENGAGEMENT_PANEL_IDS.CHAT, {
                msgType: ENGAGEMENT_MESSAGE_TYPE.BADGE
              });
            },
            onClick: () => {
              setActivePanel(ENGAGEMENT_PANEL_IDS.CHAT);
            }
          }
        }
      });
      break;
    case 'pending_questions_available':
      // Show badge
      notifyMessage(ENGAGEMENT_PANEL_IDS.LIVE_QA, {
        msgType: ENGAGEMENT_MESSAGE_TYPE.BADGE
      });
      break;
    default:
      LOG.debug(`Unsupported type: ${msgType}`);
      break;
  }
};

const generateRealTimeUpdatePayload = type => {
  switch (type) {
    case 'question_answered':
      return {
        type,
        data: {
          id: 'QUESTION_ID',
          text: 'What are the basic recommendations for running a Hybrid event?',
          answer: 'The answer goes here....'
        }
      };
    case 'poll_question_opened':
      return {
        type,
        data: {
          pollId: 'POLL_ID',
          questionId: 'POLLING_QUESTION_ID',
          text: 'Who is the best player of all time in NBA history?'
        }
      };
    case 'pending_questions_available':
      return {
        type,
        data: {
          questionId: 'QA_QUESTION_ID',
          text: 'How can I successfully deliver a hybrid event?'
        }
      };
    default:
      return { type };
  }
};

/**
 * Hook to run a simulation of the messages the BE will send to the application. This simulation
 * is only ran in lower environments.
 */
const useSimulateEngagementMessages = ({
  eventId,
  sessionId,
  attendeeId,
  isModerator,
  skip = false,
  onUpdate
}) => {
  const messagesCountRef = useRef();
  const onRealTimeUpdate = useRef();
  messagesCountRef.current = messagesCountRef.current || 0;

  // Get the engagement messages api functions
  const { notifyMessage, setActivePanel } = useEngagementPanelMessages();

  const onUpdateDefault = realTimeUpdate => {
    handleSessionDetailsEngagementMessages({
      ...realTimeUpdate,
      notifyMessage,
      setActivePanel
    });
  };

  // If consumer provides an onUpdate function use it or use the default one instead
  onRealTimeUpdate.current = onUpdate || onUpdateDefault;

  // Simulate Attendee messages
  useEffect(() => {
    // Short circuit effect if consumer skips simulation
    if (skip) return;

    const messagesTypes = isModerator
      ? ['pending_questions_available']
      : [
          'question_answered',
          'poll_question_opened',
          'survey_opened',
          'chat_started'
        ];

    const simulateMessageReceived = type => {
      const messageType =
        type || messagesTypes[Math.floor(Math.random() * messagesTypes.length)];
      const message = generateRealTimeUpdatePayload(messageType);
      LOG.debug('Message Received: ', message.type);

      onRealTimeUpdate.current({
        ...message,
        eventId,
        sessionId,
        attendeeId,
        count: messagesCountRef.current
      });
    };

    // Simulate engagement messages every 20 seconds
    let chatMessageTimeout = null;
    const interval = setInterval(() => {
      messagesCountRef.current++;
      if (messagesCountRef.current <= 10) {
        if (isModerator) {
          simulateMessageReceived();
        } else {
          // Attendees are notified on more things
          simulateMessageReceived('question_answered');
          simulateMessageReceived('survey_opened');
          simulateMessageReceived('poll_question_opened');
          // 4th message should not display a toast but a badge instead
          chatMessageTimeout = setTimeout(() => {
            simulateMessageReceived('chat_started');
          }, 2000);
        }
      }
    }, 20000);

    // Clean up interval
    return () => {
      clearInterval(interval);
      if (chatMessageTimeout) {
        clearTimeout(chatMessageTimeout);
      }
    };
  }, [isModerator, skip, attendeeId, sessionId, eventId]);
};

export default useSimulateEngagementMessages;
